/**
 * @module User
 */
import { LoggerService, Service, UserInfoService } from "rainbow-web-sdk/services";
import { ConnectedUser, RBEvent, User } from "rainbow-web-sdk/models";

import { Subject, Subscription, filter } from "rxjs";
import { UsersByEmailsResult } from "../../models/user.model";

/** @internal */
export const USER_SVC = 'UserService';

/**
 * Users service events
 * Can listen to it via `UserService.subscribe()` API.
 * @eventProperty
 */
export enum UserServiceEvents {
    /**
    * @eventProperty
    */
    ON_USER_CHANGED = "ON_USER_CHANGED",
}

/**
 * User Service interface
 */
export interface UserService {

    /**
     * The current connectedUser
     */
    connectedUser?: ConnectedUser;

    /**
     * Subscribe to updates from the user service, all events are of RBEvent<UserServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - Array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(callback: (event: RBEvent<UserServiceEvents>) => any, eventNames?: UserServiceEvents | UserServiceEvents[]): Subscription;

    /**
     * Get a user by one of its identifier
     * The user is first search in service cache then server side.
     * @param userRef - a user jid or user dbId or user jid tel
     */
    getUserById(userRef: string): Promise<User | undefined>;

    /**
     * Retrieves users matching the given e-mail addresses.
     *
     * Each e-mail is looked up against the Rainbow directory. Found users are
     * returned both as an indexed map (keyed by login e-mail) and as a flat
     * array. E-mail addresses that did not match any Rainbow user are returned
     * in the `emails` array so the caller can handle them as guest invitations.
     *
     * @param emails - List of e-mail addresses to resolve.
     * @returns An object containing:
     *   - `contacts` — a map of `loginEmail → User` for matched users,
     *   - `contactsArray` — the same matched users as a flat array,
     *   - `emails` — e-mail addresses that could not be resolved to a user.
     */
    getUsersByEmails(emails: string[]): Promise<any>;

    /**
     * Computes a deterministic color string associated with a user's display name.
     * The same display name will always produce the same color, making it suitable
     * for use as a user avatar background color.
     * @param displayName - The display name of the user
     * @returns A color string (e.g. a hex or CSS color value)
     */
    computeUserColor(displayName: string): { colorIndex: number; color: string; colorName: string; };

    /**
     * Computes the avatar representation for a user.
     * If no avatar update timestamp is provided, generates a default avatar image from the user's initials and color.
     * Otherwise, returns a URL pointing to the server-side avatar image.
     * @param objId - The unique identifier of the user
     * @param initials - The user's initials, used to generate a default avatar
     * @param color - The background color used when generating the default avatar
     * @param size - The desired avatar size in pixels
     * @param lastAvatarUpdate - Timestamp of the last avatar update; falsy if no custom avatar exists
     * @returns An `HTMLImageElement` for a generated default avatar, an object with a `src` URL for a server avatar, or `null` if no initials are available
     */
    computeUserAvatar(objId: string, initials: string, color: string, size: number, lastAvatarUpdate: any): HTMLImageElement | { src: string; } | null;

    /**
     * Computes the display name and initials for a user from their first and last name.
     * The display order (first-last or last-first) is determined by the application settings.
     * @param firstName - The user's first name
     * @param lastName - The user's last name
     * @returns An object containing the formatted `displayName` and the two-letter `initials`
     */
    computeUserName(firstName: string, lastName: string): { displayName: string; initials: string; };
}

/**
 * User Service implementation
 * @internal
 */
export class UserServiceRB extends Service implements UserService {

    private rxSubject: Subject<RBEvent<UserServiceEvents>> = new Subject<RBEvent<UserServiceEvents>>();
    private readonly logger: LoggerService = LoggerService.getInstance();
    private readonly userInfoService: UserInfoService = UserInfoService.getInstance();
    private contactService?: any;

    /**
      * Returns the Favorite service singleton
      */
    public static getInstance(): UserServiceRB {
        return Service.create(USER_SVC, UserServiceRB);
    }

    public static build(): UserServiceRB {
        return new UserServiceRB();
    }

    private constructor() { super(); }

    public get connectedUser(): ConnectedUser { return this.contactService.userContact; }

    public subscribe(handler: (event: RBEvent<UserServiceEvents>) => any, eventNames?: UserServiceEvents | UserServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<UserServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<UserServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[UserService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /* @internal */
    public configure(contactService: any) { this.contactService = contactService; }

    /**
     * User service events send method
     * Can listen to it via UserService.subscribe() API
     * @param name - the name of the event
     * @param data - the data to be send (an object)
     */
    public sendEvent(name: UserServiceEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create<UserServiceEvents, any>(name, data));
    }

    /**
     * Get a user by one of its identifier
     * The user is first search in service cache then server side.
     * @param userRef - a user jid or user dbId or user jid tel
     */
    public getUserById(userRef: string): Promise<User | undefined> {
        return this.contactService?.getContact(userRef);
    }

    public getUsersByEmails(emails: string[]): Promise<UsersByEmailsResult> {
        return this.contactService?.getContactsByEmails(emails);
    }

    /**
     * Check if the jid/dbId belongs to the connected user
     * @param userRef - a user jid or user dbId or user jid tel
     */
    public isConnectedUser(userRef: string): boolean {
        return this.contactService?.isUserContact(this.contactService?.getContact(userRef));
    }

    public computeUserColor(displayName: string): { colorIndex: number; color: string; colorName: string; } {
        return this.userInfoService.computeUserColor(displayName);
    }

    public computeUserAvatar(objId: string, initials: string, color: string, size: number, lastAvatarUpdate: any): HTMLImageElement | { src: string; } | null {
        return this.userInfoService.getAvatarImage(objId, initials, color, size, lastAvatarUpdate);
    }

    public computeUserName(firstName: string, lastName: string): { displayName: string; initials: string; } {
        return this.userInfoService.getNameInformation(firstName, lastName);
    }
}
